import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { FaEdit, FaTrashAlt, FaPlusCircle, FaGlobe, FaWordpress, FaServer, FaLock } from 'react-icons/fa';
import config from '../config';

interface WebsiteTemplate {
  id: number;
  template_title: string;
  industries: string;
  why_use_case: string;
  domain_name: string;
  site_url: string;
  wp_admin_url: string;
  wp_username: string;
  hosting_provider: string;
  hosting_login_url: string;
  hosting_username: string;
  wp_theme: string;
  notes: string;
}

interface FormData {
  id?: number;
  template_title: string;
  industries: string;
  why_use_case: string;
  domain_name: string;
  site_url: string;
  wp_admin_url: string;
  wp_username: string;
  wp_password: string; // Only used for form, not stored in state
  hosting_provider: string;
  hosting_login_url: string;
  hosting_username: string;
  hosting_password: string; // Only used for form, not stored in state
  wp_theme: string;
  notes: string;
}

const initialFormState: FormData = {
  template_title: '',
  industries: '',
  why_use_case: '',
  domain_name: '',
  site_url: '',
  wp_admin_url: '',
  wp_username: '',
  wp_password: '',
  hosting_provider: '',
  hosting_login_url: '',
  hosting_username: '',
  hosting_password: '',
  wp_theme: '',
  notes: ''
};

const WebsiteTemplates: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [currentTemplateId, setCurrentTemplateId] = useState<number | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<number | null>(null);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({
    wp_password: false,
    hosting_password: false
  });
  
  const queryClient = useQueryClient();

  // Fetch templates
  const { data: templates, isLoading, error } = useQuery<{ records: WebsiteTemplate[] }>({
    queryKey: ['website-templates'],
    queryFn: async () => {
      const response = await axios.get(`${config.apiBaseUrl}/website_templates/read.php`);
      return response.data;
    }
  });

  // Mutation for creating a template
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await axios.post(`${config.apiBaseUrl}/website_templates/create.php`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-templates'] });
      setIsModalOpen(false);
      setFormData(initialFormState);
    }
  });

  // Mutation for updating a template
  const updateMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await axios.post(`${config.apiBaseUrl}/website_templates/update.php`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-templates'] });
      setIsModalOpen(false);
      setFormData(initialFormState);
    }
  });

  // Mutation for deleting a template
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await axios.post(`${config.apiBaseUrl}/website_templates/delete.php`, { id });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['website-templates'] });
      setIsDeleteModalOpen(false);
      setCurrentTemplateId(null);
    }
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (currentTemplateId) {
      // Update existing template
      updateMutation.mutate({ ...formData, id: currentTemplateId });
    } else {
      // Create new template
      createMutation.mutate(formData);
    }
  };

  const openEditModal = (template: WebsiteTemplate) => {
    setCurrentTemplateId(template.id);
    setFormData({
      template_title: template.template_title,
      industries: template.industries || '',
      why_use_case: template.why_use_case || '',
      domain_name: template.domain_name || '',
      site_url: template.site_url || '',
      wp_admin_url: template.wp_admin_url || '',
      wp_username: template.wp_username || '',
      wp_password: '', // Don't pre-fill passwords for security
      hosting_provider: template.hosting_provider || '',
      hosting_login_url: template.hosting_login_url || '',
      hosting_username: template.hosting_username || '',
      hosting_password: '', // Don't pre-fill passwords for security
      wp_theme: template.wp_theme || '',
      notes: template.notes || ''
    });
    setIsModalOpen(true);
  };

  const openAddModal = () => {
    setCurrentTemplateId(null);
    setFormData(initialFormState);
    setIsModalOpen(true);
  };

  const openDeleteModal = (id: number) => {
    setCurrentTemplateId(id);
    setIsDeleteModalOpen(true);
  };

  const togglePasswordVisibility = (field: 'wp_password' | 'hosting_password') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const toggleExpand = (id: number) => {
    setExpandedTemplate(expandedTemplate === id ? null : id);
  };

  if (isLoading) return <div className="p-4">Loading website templates...</div>;
  if (error) return <div className="p-4 text-red-500">Error loading templates: {(error as Error).message}</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Website Templates</h2>
        <button
          onClick={openAddModal}
          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center gap-2"
        >
          <FaPlusCircle /> Add Template
        </button>
      </div>

      {/* Templates list */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {templates?.records.length === 0 ? (
          <p className="p-6 text-center text-gray-500">No website templates found. Create one to get started.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {templates?.records.map(template => (
              <li key={template.id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">{template.template_title}</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      <strong>Industries:</strong> {template.industries || 'N/A'}
                    </p>
                    
                    <div className="mt-2 flex flex-wrap gap-2">
                      {template.site_url && (
                        <a 
                          href={template.site_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
                        >
                          <FaGlobe className="mr-1" /> Visit Site
                        </a>
                      )}
                      
                      {template.wp_admin_url && (
                        <a 
                          href={template.wp_admin_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 hover:bg-purple-100"
                        >
                          <FaWordpress className="mr-1" /> WP Admin
                        </a>
                      )}
                      
                      {template.hosting_login_url && (
                        <a 
                          href={template.hosting_login_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-50 text-gray-700 hover:bg-gray-100"
                        >
                          <FaServer className="mr-1" /> Hosting
                        </a>
                      )}
                    </div>
                    
                    {/* Collapsible details */}
                    <button
                      onClick={() => toggleExpand(template.id)}
                      className="mt-3 text-sm text-primary-600 hover:text-primary-800"
                    >
                      {expandedTemplate === template.id ? 'Hide details' : 'Show details'}
                    </button>
                    
                    {expandedTemplate === template.id && (
                      <div className="mt-4 space-y-3 bg-gray-50 p-4 rounded-md">
                        <div>
                          <h4 className="font-medium">Why Use This Template</h4>
                          <p className="text-sm mt-1">{template.why_use_case || 'No description provided.'}</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="font-medium">Website Details</h4>
                            <ul className="mt-1 space-y-1 text-sm">
                              <li><strong>Domain:</strong> {template.domain_name || 'N/A'}</li>
                              <li><strong>WordPress Theme:</strong> {template.wp_theme || 'N/A'}</li>
                              <li><strong>WP Username:</strong> {template.wp_username || 'N/A'}</li>
                              <li className="flex items-center">
                                <strong>WP Password:</strong>
                                <span className="text-gray-500 italic ml-1">Hidden for security</span>
                                <FaLock className="ml-2 text-gray-400" />
                              </li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium">Hosting Details</h4>
                            <ul className="mt-1 space-y-1 text-sm">
                              <li><strong>Provider:</strong> {template.hosting_provider || 'N/A'}</li>
                              <li><strong>Username:</strong> {template.hosting_username || 'N/A'}</li>
                              <li className="flex items-center">
                                <strong>Password:</strong>
                                <span className="text-gray-500 italic ml-1">Hidden for security</span>
                                <FaLock className="ml-2 text-gray-400" />
                              </li>
                            </ul>
                          </div>
                        </div>
                        
                        {template.notes && (
                          <div>
                            <h4 className="font-medium">Notes</h4>
                            <p className="text-sm mt-1">{template.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(template)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                      title="Edit template"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => openDeleteModal(template.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                      title="Delete template"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">
                {currentTemplateId ? 'Edit Template' : 'Add New Template'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Basic Information</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label htmlFor="template_title" className="block text-sm font-medium text-gray-700">
                        Template Title*
                      </label>
                      <input
                        type="text"
                        id="template_title"
                        name="template_title"
                        value={formData.template_title}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="industries" className="block text-sm font-medium text-gray-700">
                        Industries
                      </label>
                      <input
                        type="text"
                        id="industries"
                        name="industries"
                        value={formData.industries}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="why_use_case" className="block text-sm font-medium text-gray-700">
                        Why Use This Template
                      </label>
                      <textarea
                        id="why_use_case"
                        name="why_use_case"
                        value={formData.why_use_case}
                        onChange={handleInputChange}
                        rows={3}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Website Details */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Website Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="domain_name" className="block text-sm font-medium text-gray-700">
                        Domain Name
                      </label>
                      <input
                        type="text"
                        id="domain_name"
                        name="domain_name"
                        value={formData.domain_name}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="site_url" className="block text-sm font-medium text-gray-700">
                        Site URL
                      </label>
                      <input
                        type="url"
                        id="site_url"
                        name="site_url"
                        value={formData.site_url}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="wp_admin_url" className="block text-sm font-medium text-gray-700">
                        WordPress Admin URL
                      </label>
                      <input
                        type="url"
                        id="wp_admin_url"
                        name="wp_admin_url"
                        value={formData.wp_admin_url}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="wp_theme" className="block text-sm font-medium text-gray-700">
                        WordPress Theme
                      </label>
                      <input
                        type="text"
                        id="wp_theme"
                        name="wp_theme"
                        value={formData.wp_theme}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="wp_username" className="block text-sm font-medium text-gray-700">
                        WordPress Username
                      </label>
                      <input
                        type="text"
                        id="wp_username"
                        name="wp_username"
                        value={formData.wp_username}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="wp_password" className="block text-sm font-medium text-gray-700">
                        WordPress Password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          type={showPasswords.wp_password ? "text" : "password"}
                          id="wp_password"
                          name="wp_password"
                          value={formData.wp_password}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder={currentTemplateId ? "Leave blank to keep current password" : ""}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          onClick={() => togglePasswordVisibility('wp_password')}
                        >
                          {showPasswords.wp_password ? <span>Hide</span> : <span>Show</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hosting Details */}
                <div>
                  <h3 className="text-lg font-medium mb-3">Hosting Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="hosting_provider" className="block text-sm font-medium text-gray-700">
                        Hosting Provider
                      </label>
                      <input
                        type="text"
                        id="hosting_provider"
                        name="hosting_provider"
                        value={formData.hosting_provider}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hosting_login_url" className="block text-sm font-medium text-gray-700">
                        Hosting Login URL
                      </label>
                      <input
                        type="url"
                        id="hosting_login_url"
                        name="hosting_login_url"
                        value={formData.hosting_login_url}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hosting_username" className="block text-sm font-medium text-gray-700">
                        Hosting Username
                      </label>
                      <input
                        type="text"
                        id="hosting_username"
                        name="hosting_username"
                        value={formData.hosting_username}
                        onChange={handleInputChange}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="hosting_password" className="block text-sm font-medium text-gray-700">
                        Hosting Password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          type={showPasswords.hosting_password ? "text" : "password"}
                          id="hosting_password"
                          name="hosting_password"
                          value={formData.hosting_password}
                          onChange={handleInputChange}
                          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 pl-3 pr-10 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                          placeholder={currentTemplateId ? "Leave blank to keep current password" : ""}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                          onClick={() => togglePasswordVisibility('hosting_password')}
                        >
                          {showPasswords.hosting_password ? <span>Hide</span> : <span>Show</span>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                
                {/* Form Actions */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save Template'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4">Delete Template</h2>
              <p className="mb-6">Are you sure you want to delete this template? This action cannot be undone.</p>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => currentTemplateId && deleteMutation.mutate(currentTemplateId)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebsiteTemplates; 